"use client"

import { useEffect, useMemo, useRef } from "react"
import { Carousel } from "./carousel"
import { useSharedData } from "@/app/store/useDestinationSharedData"
import gsap from "@/lib/gsap-init"
import { useLocalizedField } from '@/hooks/useLocalizedField'

export function DestinationsSection() {
  const { destinations, loadingDestinations, errorDestinations, fetchDestinations } = useSharedData()
  const sectionRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const { getLocalizedField } = useLocalizedField()

  useEffect(() => {
    // Wait for refs to be available and destinations to be loaded
    if (!sectionRef.current || !carouselRef.current) return
    if (loadingDestinations) return // Don't animate until content is loaded

    const ctx = gsap.context(() => {
      // Set initial state
      gsap.set(carouselRef.current, { opacity: 0, y: 60 })

      // Carousel animation
      gsap.to(carouselRef.current, {
        opacity: 1,
        y: 0,
        duration: 2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          end: "top 50%",
          scrub: 0.5,
          toggleActions: "play none none reverse",
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [loadingDestinations, destinations]) // Re-run when destinations load

  // Load destinations on mount
  useEffect(() => {
    fetchDestinations()
  }, [fetchDestinations])

  // Transform backend destinations → Carousel format
  const formattedDestinations = useMemo(() => {
    if (!destinations || destinations.length === 0) return []

    return destinations.map((d) => ({
      id: d.slug || d.name,
      title: getLocalizedField(d, 'name'),
      name: getLocalizedField(d, 'name'),
      description: getLocalizedField(d, 'short_description'),
      image: d.gallery[1] || d.cover,
    }))
  }, [destinations])

  // Select 8 random items
  const randomDestinations = useMemo(() => {
    if (formattedDestinations.length === 0) return []
    return [...formattedDestinations].sort(() => Math.random() - 0.5).slice(0, 8)
  }, [formattedDestinations])

  return (
    <section ref={sectionRef} className="py-4 px-4 bg-background">
      <div className="max-w-7xl mx-auto">
        {loadingDestinations && <p className="text-lg text-muted-foreground">Loading destinations...</p>}

        {errorDestinations && <p className="text-red-500">{errorDestinations}</p>}

        {!loadingDestinations && randomDestinations.length > 0 && (
          <div ref={carouselRef}>
            <Carousel 
              items={randomDestinations} 
              showItemsCount={4.5}
              title="Discover Tunisia"
              paragraph="Explore the most beautiful destinations across Tunisia"
              type="destination"
            />
          </div>
        )}
      </div>
    </section>
  )
}